import { Auth0Provider } from "@auth0/auth0-react";
import * as Sentry from "@sentry/react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import React from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";

import { AppUpdater } from "~/components/layout/app-updater";
import { Toast } from "~/components/ui/toast";
import { TooltipProvider } from "~/components/ui/tooltip";
import usePageTracking from "~/hooks/use-page-tracking";
import "~/libs/i18n";
import "~/libs/instrument";
import { NotFound } from "~/views/not-found/not-found";
import { Onboarding } from "~/views/onboarding/onboarding";

import { BasicBrandInfo } from "../components/basic-brand-info/basic-brand-info";
import { FullBrandInfo } from "../components/full-brand-info/full-brand-info";
import { ProtectedRoute } from "../components/protected-route/protected-route";
import { RedirectIfLogged } from "../components/redirect-if-logged/redirect-if-logged";
import { ConfirmPasswordlessCode } from "../views/confirm-passwordless-code/confirm-passwordless-code";
import { Dashboard } from "../views/dashboard/dashboard";
import { ForgotPasswordConfirmation } from "../views/forgot-password-confirmation/forgot-password-confirmation";
import { ForgotPasswordEmail } from "../views/forgot-password-email/forgot-password-email";
import { LimitedAccess } from "../views/limited-access/limited-access";
import { LimitedSettings } from "../views/limited-access/limited-settings";
import { LoginSuccessful } from "../views/login-successful/login-successful";
import { LoginWithPassword } from "../views/login-with-password/login-with-password";
import { Login } from "../views/login/login";
import { LogoutSuccessful } from "../views/logout-successful/logout-successful";
import { OnboardingUpdateProfile } from "../views/onboarding-update-profile/onboarding-update-profile";
import { Signup } from "../views/signup/signup";
import { Version } from "../views/version/version";
import { Welcome } from "../views/welcome/welcome";

export type BrandMatch = {
  brandId: string;
  step?: string;
  path?: string;
  orderId?: string;
};

export type BrandMatchWithSubStep = {
  substep?: string;
} & BrandMatch;

const queryClient = new QueryClient();

export default function Root() {
  return (
    <Sentry.ErrorBoundary>
      <Auth0Provider
        domain={import.meta.env.REACT_APP_AUTH0_DOMAIN as string}
        clientId={import.meta.env.REACT_APP_AUTH0_CLIENT_ID as string}
        authorizationParams={{
          audience: `${import.meta.env.REACT_APP_AUTH0_AUDIENCE as string}`,
          redirect_uri: `${window.location.origin}/login-successful`,
          scope: "openid profile email offline_access",
        }}
      >
        <QueryClientProvider client={queryClient}>
          <TooltipProvider>
            <BrowserRouter>
              <PageTracking />
              <Routes>
                <Route path="/" element={<NotFound />} />
                <Route
                  path="/login-successful/*"
                  element={<LoginSuccessful />}
                />
                <Route
                  path="/logout-successful/*"
                  element={<LogoutSuccessful />}
                />
                <Route path="/version/*" element={<Version />} />

                <Route
                  path="/:brandId/*"
                  element={
                    <BasicBrandInfo>
                      <Routes>
                        <Route
                          path="onboarding/:step?/:substep?/*"
                          element={<Onboarding />}
                        />

                        <Route path="welcome/*" element={<Welcome />} />
                        <Route
                          path="login/*"
                          element={
                            <RedirectIfLogged>
                              <Login />
                            </RedirectIfLogged>
                          }
                        />
                        <Route
                          path="login-with-password/*"
                          element={
                            <RedirectIfLogged>
                              <LoginWithPassword />
                            </RedirectIfLogged>
                          }
                        />
                        <Route
                          path="signup/*"
                          element={
                            <RedirectIfLogged>
                              <Signup />
                            </RedirectIfLogged>
                          }
                        />
                        <Route
                          path="confirm-passwordless-code/*"
                          element={
                            <RedirectIfLogged>
                              <ConfirmPasswordlessCode />
                            </RedirectIfLogged>
                          }
                        />
                        <Route
                          path="forgot-password/*"
                          element={
                            <RedirectIfLogged>
                              <ForgotPasswordEmail />
                            </RedirectIfLogged>
                          }
                        />
                        <Route
                          path="forgot-password-confirmation/*"
                          element={
                            <RedirectIfLogged>
                              <ForgotPasswordConfirmation />
                            </RedirectIfLogged>
                          }
                        />

                        <Route
                          path="onboarding-update-profile/*"
                          element={
                            <ProtectedRoute>
                              <OnboardingUpdateProfile />
                            </ProtectedRoute>
                          }
                        />

                        <Route
                          path="limited-access/*"
                          element={
                            <ProtectedRoute>
                              <LimitedAccess />
                            </ProtectedRoute>
                          }
                        />

                        <Route
                          path="limited-settings/*"
                          element={
                            <ProtectedRoute>
                              <LimitedSettings />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="*"
                          element={
                            <ProtectedRoute>
                              <FullBrandInfo>
                                <Dashboard />
                              </FullBrandInfo>
                            </ProtectedRoute>
                          }
                        />
                      </Routes>
                    </BasicBrandInfo>
                  }
                />
              </Routes>
            </BrowserRouter>
            <Toast />
            <AppUpdater />
            <ReactQueryDevtools initialIsOpen={false} />
          </TooltipProvider>
        </QueryClientProvider>
      </Auth0Provider>
    </Sentry.ErrorBoundary>
  );
}

function PageTracking() {
  usePageTracking();

  return null;
}
